import moment from 'moment';

// Shared utility to group expenses
const groupBy = (expenses, keyGetter) => {
    const map = new Map();
    expenses.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return map;
};

// 1. Daily Expenses (Selected Month)
export const getDailyExpenses = (expenses, selectedMonthMoment = moment()) => {
    // Filter to selected month
    const validExpenses = expenses.filter(expense => moment(expense.createdAt).isSame(selectedMonthMoment, 'month'));
    // Sort expenses by createdAt ascending
    const sorted = [...validExpenses].sort((a, b) => a.createdAt - b.createdAt);
    
    // Group only by Day to prevent crowding the XAxis (Month and Year are already known by UI)
    const grouped = groupBy(sorted, expense => moment(expense.createdAt).format('Do'));
    
    const data = [];
    grouped.forEach((groupExpenses, date) => {
        const totalAmount = groupExpenses.reduce((sum, item) => sum + item.amount, 0);
        data.push({ name: date, amount: totalAmount });
    });
    return data;
};

// 2. Weekly Expenses (grouped by week of the year, selected month)
export const getWeeklyExpenses = (expenses, selectedMonthMoment = moment()) => {
    const validExpenses = expenses.filter(expense => moment(expense.createdAt).isSame(selectedMonthMoment, 'month'));
    const sorted = [...validExpenses].sort((a, b) => a.createdAt - b.createdAt);
    
    // Group by 'YYYY-WW' (e.g., 2023-42)
    const grouped = groupBy(sorted, expense => `${moment(expense.createdAt).format('YYYY')} - W${moment(expense.createdAt).isoWeek()}`);

    const data = [];
    grouped.forEach((groupExpenses, weekStr) => {
        const totalAmount = groupExpenses.reduce((sum, item) => sum + item.amount, 0);
        data.push({ name: weekStr, amount: totalAmount });
    });
    return data;
};

// 3. Week and Weekend of the month (Selected Month)
export const getWeekendVsWeekdayExpenses = (expenses, selectedMonthMoment = moment()) => {
    const validExpenses = expenses.filter(expense => moment(expense.createdAt).isSame(selectedMonthMoment, 'month'));
    let weekdayTotal = 0;
    let weekendTotal = 0;

    validExpenses.forEach((expense) => {
        const dayOfWeek = moment(expense.createdAt).isoWeekday(); // 1=Mon, 7=Sun
        if (dayOfWeek === 6 || dayOfWeek === 7) {
            weekendTotal += expense.amount;
        } else {
            weekdayTotal += expense.amount;
        }
    });

    return [
        { name: 'Weekdays', amount: weekdayTotal },
        { name: 'Weekends', amount: weekendTotal }
    ];
};

// 4. Monthly Spend (Max 12 Months trailing from Selected Month)
export const getMonthlyExpenses = (expenses, selectedMonthMoment = moment()) => {
    const validExpenses = expenses.filter(expense => 
        moment(expense.createdAt).isAfter(moment(selectedMonthMoment).subtract(12, 'months').startOf('month')) &&
        moment(expense.createdAt).isSameOrBefore(moment(selectedMonthMoment).endOf('month'))
    );
    const sorted = [...validExpenses].sort((a, b) => a.createdAt - b.createdAt);
    
    // Group by 'MMM YYYY'
    const grouped = groupBy(sorted, expense => moment(expense.createdAt).format('MMM YYYY'));

    const data = [];
    grouped.forEach((groupExpenses, monthStr) => {
        const totalAmount = groupExpenses.reduce((sum, item) => sum + item.amount, 0);
        data.push({ name: monthStr, amount: totalAmount });
    });
    return data;
};

// 5. Spend by Day of Week (Selected Month)
export const getSpendByDayOfWeek = (expenses, selectedMonthMoment = moment()) => {
    const validExpenses = expenses.filter(expense => moment(expense.createdAt).isSame(selectedMonthMoment, 'month'));
    const grouped = groupBy(validExpenses, expense => moment(expense.createdAt).format('dddd'));
    
    // Sort logic to ensure logical calendar day order
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const data = [];
    days.forEach(day => {
        const groupExpenses = grouped.get(day) || [];
        const totalAmount = groupExpenses.reduce((sum, item) => sum + item.amount, 0);
        data.push({ name: day.substring(0,3), amount: totalAmount }); 
    });
    
    return data;
};

// 6. Top Highest Expenses (Selected Month)
export const getTopExpenses = (expenses, selectedMonthMoment = moment()) => {
    const validExpenses = expenses.filter(expense => moment(expense.createdAt).isSame(selectedMonthMoment, 'month'));
    
    // Isolate Top 5 highest single purchases overall for the selected month
    const sortedDesc = [...validExpenses].sort((a, b) => b.amount - a.amount);
    const top5 = sortedDesc.slice(0, 5);
    
    return top5.map(item => ({
        id: item.id,
        name: item.description.substring(0, 15) + (item.description.length > 15 ? '...' : ''),
        amount: item.amount
    }));
};

// 7. Overall Month Statistics
export const getMonthStatistics = (expenses, selectedMonthMoment = moment()) => {
    const validExpenses = expenses.filter(expense => moment(expense.createdAt).isSame(selectedMonthMoment, 'month'));
    
    if (validExpenses.length === 0) {
        return { total: 0, count: 0, averageDaily: 0, highest: 0, lowest: 0, busiestDay: 'N/A' };
    }
    
    const amounts = validExpenses.map(e => e.amount);
    const total = amounts.reduce((sum, val) => sum + val, 0);
    const count = validExpenses.length;
    
    // Calculate precise Daily Average (divide by today's date if current month, otherwise total days in selected month)
    const isCurrentMonth = moment().isSame(selectedMonthMoment, 'month');
    const daysToDivide = isCurrentMonth ? Math.max(moment().date(), 1) : selectedMonthMoment.daysInMonth();
    const averageDaily = total / daysToDivide;
    
    const highest = Math.max(...amounts);
    const lowest = Math.min(...amounts);
    
    // Busiest Calendar Day (Highest Total Spend)
    const groupedByDay = groupBy(validExpenses, e => moment(e.createdAt).format('Do'));
    let peakDay = '';
    let peakDayTotal = 0;
    groupedByDay.forEach((groupExp, date) => {
        const dayTotal = groupExp.reduce((sum, e) => sum + e.amount, 0);
        if (dayTotal > peakDayTotal) {
            peakDayTotal = dayTotal;
            peakDay = date;
        }
    });
    
    return {
        total: parseFloat(total.toFixed(2)),
        count,
        averageDaily: parseFloat(averageDaily.toFixed(2)),
        highest,
        lowest,
        busiestDay: peakDay ? `${peakDay} (Rs ${peakDayTotal})` : 'N/A'
    };
};
